import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { MapContainer, TileLayer, Marker, Circle, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import Seo from "@/components/Seo";
import EventCard from "@/components/EventCard";
import { useLang } from "@/context/LanguageContext";
import { haversineKm } from "@/lib/geo";
import { formatDateRange } from "@/lib/format";
import { Navigation, Loader2, MapPin, Compass } from "lucide-react";

const RADII = [25, 50, 75, 100];
const eventIcon = (promoted) => L.divIcon({ className: "", html: `<div class="pagan-marker ${promoted ? "promoted" : ""}"></div>`, iconSize: [22, 22], iconAnchor: [11, 22], popupAnchor: [0, -20] });
const userIcon = L.divIcon({ className: "", html: '<div style="width:18px;height:18px;border-radius:9999px;background:#22c55e;border:3px solid #0B0C0E;box-shadow:0 0 0 4px rgba(34,197,94,0.35)"></div>', iconSize: [18, 18], iconAnchor: [9, 9] });

function Recenter({ center, radiusKm }) {
  const map = useMap();
  useEffect(() => {
    if (center) {
      const zoom = radiusKm <= 25 ? 10 : radiusKm <= 50 ? 9 : radiusKm <= 75 ? 8 : 8;
      map.setView(center, zoom);
    }
  }, [center, radiusKm, map]);
  return null;
}

export default function Around() {
  const { lang, t, tCategory } = useLang();
  const navigate = useNavigate();
  const [coords, setCoords] = useState(null);
  const [state, setState] = useState("idle");
  const [radius, setRadius] = useState(50);

  const { data: events = [] } = useQuery({ queryKey: ["aroundEvents"], queryFn: async () => (await api.get("/events", { params: { limit: 200 } })).data });

  const locate = () => {
    if (!navigator.geolocation) { setState("denied"); return; }
    setState("locating");
    navigator.geolocation.getCurrentPosition(
      (pos) => { setCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude }); setState("ok"); },
      () => setState("denied"),
      { enableHighAccuracy: false, timeout: 10000, maximumAge: 600000 }
    );
  };
  useEffect(() => { locate(); /* eslint-disable-next-line */ }, []);

  const nearby = useMemo(() => {
    if (!coords) return [];
    const now = new Date();
    return events
      .filter((e) => e.lat && e.lng && new Date(e.end_date || e.start_date) >= now)
      .map((e) => ({ ...e, _distanceKm: haversineKm(coords.lat, coords.lng, e.lat, e.lng) }))
      .filter((e) => e._distanceKm <= radius)
      .sort((a, b) => a._distanceKm - b._distanceKm);
  }, [coords, events, radius]);

  return (
    <>
      <Seo title={t("around_title")} description={t("around_desc")} canonicalPath="/autour" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-12">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8 border-b border-amber-900/30 pb-4">
          <div>
            <div className="font-mono text-xs uppercase tracking-[0.25em] text-amber-500/90 mb-2 flex items-center gap-2"><Compass className="w-3.5 h-3.5" /> {t("nearyou_tag")}</div>
            <h1 className="font-serif text-3xl sm:text-4xl uppercase tracking-tight text-amber-100">{t("around_title")}</h1>
            <p className="text-stone-400 mt-2 max-w-2xl">{t("around_desc")}</p>
          </div>
          {state === "ok" && (
            <div className="flex items-center gap-1.5" data-testid="around-radius-group">
              {RADII.map((r) => (
                <button key={r} data-testid={`around-radius-${r}`} onClick={() => setRadius(r)}
                  className={`px-3.5 py-1.5 rounded-full font-mono text-xs border transition-all ${radius === r ? "bg-amber-600 text-stone-950 border-amber-400 font-bold" : "bg-[#181A20] text-stone-300 border-[#272A34] hover:border-amber-500/50"}`}>
                  {r} km
                </button>
              ))}
            </div>
          )}
        </div>

        {state === "locating" && (
          <div className="flex items-center gap-3 text-stone-400 py-16" data-testid="around-locating"><Loader2 className="w-5 h-5 animate-spin text-amber-500" /> {t("nearyou_locating")}</div>
        )}

        {state === "denied" && (
          <div className="rounded-xl border border-[#272A34] bg-[#121418] p-10 text-center" data-testid="around-denied">
            <MapPin className="w-8 h-8 text-amber-600/70 mx-auto mb-3" />
            <p className="text-stone-400 max-w-md mx-auto mb-5">{t("nearyou_denied")}</p>
            <button data-testid="around-retry-btn" onClick={locate} className="inline-flex items-center gap-2 bg-amber-600 hover:bg-amber-500 text-stone-950 font-semibold px-6 py-2.5 rounded-md transition-all"><Navigation className="w-4 h-4" /> {t("nearyou_locate")}</button>
          </div>
        )}

        {state === "ok" && coords && (
          <>
            <div className="rounded-xl overflow-hidden border border-[#272A34] shadow-[0_10px_30px_rgba(0,0,0,0.8)] mb-8" style={{ height: "520px" }} data-testid="around-map">
              <MapContainer center={[coords.lat, coords.lng]} zoom={9} style={{ height: "100%", width: "100%" }} scrollWheelZoom={false}>
                <TileLayer className="dark-tiles" url="https://tile.openstreetmap.org/{z}/{x}/{y}.png" attribution='&copy; OpenStreetMap' />
                <Recenter center={[coords.lat, coords.lng]} radiusKm={radius} />
                <Circle center={[coords.lat, coords.lng]} radius={radius * 1000} pathOptions={{ color: "#D97706", fillColor: "#D97706", fillOpacity: 0.08, weight: 1.5 }} />
                <Marker position={[coords.lat, coords.lng]} icon={userIcon} />
                {nearby.map((e) => (
                  <Marker key={e.slug} position={[e.lat, e.lng]} icon={eventIcon(e.promoted)}>
                    <Popup>
                      <div className="min-w-[180px]">
                        <div className="text-[10px] font-mono uppercase tracking-wider text-amber-500 mb-1">{tCategory(e.category)} · {e._distanceKm} km</div>
                        <div className="font-serif text-sm text-stone-100 mb-1 leading-snug">{e.title}</div>
                        <div className="text-xs text-stone-400 mb-2">{e.city} · {formatDateRange(e.start_date, e.end_date, lang)}</div>
                        <button data-testid={`around-popup-link-${e.slug}`} onClick={() => navigate(`/events/${e.slug}`)} className="text-xs font-semibold text-amber-400 hover:text-amber-300 underline">→</button>
                      </div>
                    </Popup>
                  </Marker>
                ))}
              </MapContainer>
            </div>

            <h2 className="font-serif text-2xl text-stone-100 mb-6 border-b border-amber-900/30 pb-4">{t("around_list")} ({nearby.length})</h2>
            {nearby.length === 0 ? (
              <p className="text-stone-500 py-6" data-testid="around-none">{t("nearyou_none")}</p>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="around-list">
                {nearby.slice(0, 24).map((e, i) => <EventCard key={e.slug} event={e} index={i} distanceKm={e._distanceKm} />)}
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
}
